import { FC, useEffect, useState } from 'react';
import { FormSelect } from '../../../../../../../../shared/components/form';
import { Table } from '../../../../../../../../shared/components/table/table.component.tsx';
import { Column } from '../../../../../../../../shared/components/table/components/common.components.tsx';
import { useTranslation } from 'react-i18next';
import { Stack, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ContentLoading } from '../../../../../../../../shared/components/loading/loading.component.tsx';
import {
  bakery2bClientsState,
  IBakery2bClientsState,
  ExtendedSelectOption,
} from '../../../../../states/bakery2bClients.state.ts';
import { useFormContext } from 'react-hook-form';
import { IForm } from './connectedCustomers.popup.tsx';

export const Content: FC<IContent> = () => {
  const { t } = useTranslation();
  const [customerList, setCustomerList] = useState<IBakery2bClientsState['listGridCustomers']>([]);
  const [data, setData] = useState<
    IBakery2bClientsState['customerListConnectedWithBakery2BClient']
  >([]);
  const { watch, resetField, getValues } = useFormContext<IForm>();
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    const connectedCustomersLoadingSub = bakery2bClientsState.sub
      .connectedCustomersLoading()
      .subscribe(setLoading);
    const bakery2bClientsStateSub = bakery2bClientsState.sub
      .state()
      .subscribe(({ customerListConnectedWithBakery2BClient, listGridCustomers, action }) => {
        setData(customerListConnectedWithBakery2BClient);
        setCustomerList(listGridCustomers);
        if (action === 'addCustomerConnectedWithBakery2BClient') {
          resetField('customerId');
        }
      });
    return () => {
      connectedCustomersLoadingSub.unsubscribe();
      bakery2bClientsStateSub.unsubscribe();
    };
  }, []);

  const filterOptions = (
    options: ExtendedSelectOption[],
    { inputValue }: { inputValue: string },
  ): ExtendedSelectOption[] =>
    options.filter(
      (option) =>
        (option.customerNo?.toLowerCase().includes(inputValue?.toLowerCase()) ||
          option.internalOrFullName?.toLowerCase().includes(inputValue?.toLowerCase())) as boolean,
    );

  return (
    <ContentLoading {...{ loading }}>
      <Stack
        direction='row'
        sx={{
          gap: 2,
          mb: 2,
        }}
      >
        <FormSelect
          disabled={loading}
          filterOptions={filterOptions}
          datasetattribute='customersOptions'
          label={t('common.customer')}
          name='customerId'
          options={customerList}
          getOptionDisabled={(option) => {
            const typedOption = option as ExtendedSelectOption;
            return !!data.find((el) => el.id === typedOption.id);
          }}
        />
        <IconButton
          onClick={() =>
            bakery2bClientsState.pub.addCustomerConnectedWithBakery2BClient(getValues().customerId)
          }
          title={t('common.add')}
          disabled={loading || !watch('customerId')}
        >
          <AddIcon />
        </IconButton>
      </Stack>
      <Table data={data as []} heightOffset={300}>
        <Column
          field='customerNo'
          width={120}
          headerName={t('common.customer_no')}
          sortable={false}
        />
        <Column field='name' flex={1} headerName={t('common.name')} sortable={false} />
        <Column
          resizable={false}
          field='deletion'
          headerName=''
          width={50}
          sortable={false}
          renderCell={({ id }) => (
            <Stack
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconButton
                onClick={() =>
                  bakery2bClientsState.pub.deleteCustomerConnectedWithBakery2BClient(String(id))
                }
                title={t('common.delete')}
                size='small'
                color='default'
              >
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Stack>
          )}
        />
      </Table>
    </ContentLoading>
  );
};

type IContent = Record<string, never>;
